import { bestRouteMatch } from './bestRouteMatch'

/**
 * The page name each route shows in the browser tab — declared once, here.
 *
 * Labels reuse the sub-menu and tab labels the admin already sees, so a tab
 * reads like the screen. Resolution is `bestRouteMatch`, the same rule as
 * `ROUTE_PERMISSIONS`: a subtree entry names every page beneath it.
 *
 * `null` means the page has no name of its own. On a detail subtree the
 * entries beneath it name the entity's tabs, and the layout publishes the
 * entity name (`useDocumentTitleEntity`) once it is loaded.
 */
export const ROUTE_TITLES: Record<string, string | null> = {
  // ----------------------------------------------------------- Overview
  '/overview/home': 'Dashboard',
  '/overview/community': 'Community',
  '/overview/traffic': 'Traffic',
  '/overview/conversion': 'Conversion',
  '/overview/revenue': 'Revenue',

  // ---------------------------------------------------------------- CRM
  '/users/list': 'Clients',
  '/users/segments': 'Segments',
  '/users/transactions': 'Transactions',
  '/users/:userId': null,
  '/users/:userId/informations': 'Informations',
  '/users/:userId/transactions': 'Transactions',
  '/users/:userId/analytics': 'Analytics',
  '/users/:userId/membership': 'Membership',
  '/users/:userId/third-parties': 'Third-parties',
  '/users/:userId/activities': 'Activities',
  '/users/:userId/legals': 'Data & Legal',

  // ------------------------------------------------------------- Venues
  '/venues/list': 'All venues',
  '/venues/tags': 'Tags',
  '/venues/clusters': 'Clusters',
  '/venues/:venueSlug': null,
  '/venues/:venueSlug/overview': 'Overview',
  '/venues/:venueSlug/generals': 'Informations',
  '/venues/:venueSlug/customize': 'Customize',
  '/venues/:venueSlug/booking': 'Booking',
  '/venues/:venueSlug/nested-venues': 'Nested venues',
  '/venues/:venueSlug/whatsapp-settings': 'WhatsApp settings',
  '/venues/:venueSlug/legals': 'Legals',
  '/venues/:venueSlug/settings': 'Settings',

  // ---------------------------------------------------------- Marketing
  '/marketing/campaigns': 'Campaigns',
  '/marketing/loyalty-tiers': 'Loyalty tiers',
  '/marketing/referral': 'Referral',
  '/marketing/featured-venue': 'Featured venue',
  '/marketing/communication': 'Communication',

  // --------------------------------------------------------- Membership
  '/membership/requests': 'Requests',
  '/membership/members/list': 'Members',
  '/membership/members/:membershipProfileId': null,
  '/membership/members/:membershipProfileId/subscription': 'Subscription',
  '/membership/members/:membershipProfileId/event-participation':
    'Event participations',
  '/membership/members/:membershipProfileId/form-answers': 'Form answers',
  '/membership/members/:membershipProfileId/subscription-activities':
    'Subscription activities',
  '/membership/members/:membershipProfileId/profile-activities':
    'Profile activities',
  '/membership/members/:membershipProfileId/complementary-items':
    'Balances & credits',
  '/membership/events/list': 'Events',
  '/membership/events/:eventId': null,
  '/membership/events/:eventId/informations': 'Informations',
  '/membership/events/:eventId/participants': 'Participants',
  '/membership/events/:eventId/booking': 'Booking',
  '/membership/events/:eventId/analytics': 'Analytics',
  '/membership/events/:eventId/settings': 'Settings',
  '/membership/events-collections': 'Collections',
  '/membership/analytics': 'Analytics',
  '/membership/revenue': 'Revenue',
  '/membership/revenue/transactions': 'Transactions',
  '/membership/revenue/renewals': 'Renewals',
  '/membership/coupons': 'Coupons',
  '/membership/benefits': 'Benefits',

  // ----------------------------------------------------------- Business
  '/business/companies/list': 'Companies',
  '/business/companies/:companyId': null,
  '/business/policies': 'Policies',
  '/business/reservations': 'Reservations',

  // ----------------------------------------------------------- Settings
  // The settings home is the section itself.
  '/settings/list': null,
  '/settings/booking/availability-viewer': 'Availability viewer',
  '/settings/admins': 'Administrators',
  '/settings/admins/invites': 'Pending invitations',
  '/settings/admins/roles': 'Roles & permissions',
  '/settings/user/data-privacy': 'Data privacy',
  '/settings/integrations/sevenrooms': 'SevenRooms',
  '/settings/integrations/trivec': 'Trivec',
  '/settings/integrations/adyen': 'Adyen',
  '/settings/integrations/uber': 'Uber',
  '/settings/integrations/web-booking': 'Web booking',
  '/settings/booking/bookings': 'Booking settings',
  '/settings/booking/seating-areas': 'Seating areas',
  '/settings/booking/booking-notifications': 'Booking notifications',
  '/settings/booking/payment-link': 'Payment link',
  '/settings/bookings/notifications': 'Booking notifications',
  '/settings/membership/membership-notifications': 'Membership notifications',
  '/settings/membership/programs/list': 'Programs',
  '/settings/membership/programs/:programSlug': null,
  '/settings/membership/programs/:programSlug/informations': 'Informations',
  '/settings/membership/programs/:programSlug/concierge': 'Concierge',
  '/settings/membership/programs/:programSlug/design-branding':
    'Design & branding',
  '/settings/membership/programs/:programSlug/wallet-card': 'Wallet card',
  '/settings/membership/programs/:programSlug/form-questions': 'Form questions',
  '/settings/membership/programs/:programSlug/email-notifications':
    'Email notifications',
  '/settings/membership/programs/:programSlug/payment-settings':
    'Payment settings',
  '/settings/membership/programs/:programSlug/balance-and-credits':
    'Balances & credits',
  '/settings/membership/booklets': 'Demo tab',
  '/settings/membership/participation-statuses': 'Participation statuses',
  '/settings/membership/profiles-tags': 'Membership profiles tags',
  '/settings/membership/subscription-tags': 'Subscription tags',
  '/settings/membership/complementary-items/list': 'Balances & credits',
  '/settings/business/company-statuses': 'Company statuses',
  '/settings/business/employee-statuses': 'Employee statuses',
  '/settings/business/business-notifications': 'Business notifications',
  '/settings/business/verifier-accounts': 'Verifier accounts',
  '/settings/business/loyalty-tier': 'Business loyalty tier',
  '/settings/platforms': 'Platforms',
  '/settings/platforms/mobile-apps': 'Mobile applications',
  '/settings/platforms/whatsapp': 'WhatsApp',
  '/settings/api/external-booking-api-keys': 'External booking API keys',
  '/settings/legals/legal-documents': 'Legal documents',

  // --------------------------------------------------------------- Help
  // The help home is the section itself.
  '/help': null,

  // ------------------------------------------------- Outside a section
  '/venue-billing': 'Venue billing',
  '/403': 'Forbidden',
  '/404': 'Not found',

  // --------------------------------------------------------------- Auth
  '/auth/login': 'Login',
  '/auth/register': 'Register',
  '/auth/forgot-password': 'Forgot password',
  '/auth/reset-password': 'Reset password',
  '/auth/2FA': 'Two-factor authentication',
  '/auth/going-to-logout': 'Logging out',
}

/** The main menu entries (`MainHeader`), keyed by the first path segment. */
export const SECTION_TITLES: Record<string, string> = {
  overview: 'Overview',
  users: 'CRM',
  venues: 'Venues',
  marketing: 'Marketing',
  membership: 'Membership',
  business: 'Business',
  settings: 'Settings',
  help: 'Help',
}

/**
 * The document title for `path`, most distinctive part first since browsers
 * truncate the end:
 *
 * - a list page: `Page · Section | Tenant`
 * - a detail page, once its layout published the entity: `Entity · Tab | Tenant`
 *   (the entity already says where you are, so the section is dropped; a page
 *   without tabs reads `Entity · Section | Tenant`)
 *
 * Missing parts are dropped: an unnamed page reads `Section | Tenant`, a page
 * outside any section `Page | Tenant`.
 */
export const documentTitleFor = (
  path: string,
  tenantName: string,
  entityName?: string
): string => {
  const page = bestRouteMatch(ROUTE_TITLES, path)
  const section = SECTION_TITLES[path.split('/').filter(Boolean)[0] ?? '']
  const parts = entityName ? [entityName, page ?? section] : [page, section]
  const head = parts.filter(Boolean).join(' · ')

  return head ? `${head} | ${tenantName}` : tenantName
}
